import { Config, Club, Match, Team, Tournament } from '../types';
import { getRgba } from './utils';

export const drawExportBorder = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, config: Config) => {
  const bw = parseInt((config.exportBorderWidth || 0).toString()) || 0;
  if (bw > 0) {
    ctx.strokeStyle = config.exportBorderColor || config.primaryColor || '#eab308';
    ctx.lineWidth = bw * 2;
    ctx.strokeRect(0, 0, canvas.width, canvas.height);
  }
};

export const drawExportFooter = async (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, config: Config, clubs: Club[]) => {
  if (!config.exportShowSponsors || !clubs) return;
  const sponsors = clubs.filter((c) => c.type === 'sponsor' && c.photoUrl);
  if (sponsors.length === 0) return;

  const iconSize = 140;
  const gap = 50;
  const totalWidth = sponsors.length * iconSize + (sponsors.length - 1) * gap;
  let startX = (canvas.width - totalWidth) / 2;
  const y = canvas.height - iconSize - 65;

  ctx.textAlign = 'center';
  ctx.fillStyle = config.primaryTextColor || '#ffffff';
  ctx.font = `bold 24px "${config.fontPrimary || 'sans-serif'}"`;
  ctx.fillText('SPONSORS OFICIALES', canvas.width / 2, y - 35);

  for (const sponsor of sponsors) {
    try {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = sponsor.photoUrl!;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      ctx.beginPath();
      // @ts-ignore
      ctx.roundRect(startX, y, iconSize, iconSize, 8);
      ctx.fill();

      const scale = Math.min(iconSize / img.width, iconSize / img.height) * 0.8;
      const dw = img.width * scale;
      const dh = img.height * scale;
      const dx = startX + (iconSize - dw) / 2;
      const dy = y + (iconSize - dh) / 2;

      ctx.drawImage(img, dx, dy, dw, dh);
    } catch (e) {}
    startX += iconSize + gap;
  }
};

export const drawCanvasHeader = async (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, config: Config) => {
  let startYText = 115;
  const fontSecondary = config.fontSecondary || 'sans-serif';
  if (config.exportWithBg !== false && config.logoUrl) {
    try {
      const logo = new Image();
      logo.crossOrigin = 'Anonymous';
      logo.src = config.logoUrl;
      await new Promise((resolve, reject) => {
        logo.onload = resolve;
        logo.onerror = reject;
      });
      ctx.drawImage(logo, canvas.width / 2 - 80, 40, 160, 160);
      startYText = 245;
    } catch (e) {}
  }
  ctx.textAlign = 'center';
  ctx.fillStyle = config.primaryTextColor || config.primaryColor || '#ffffff';
  ctx.font = `bold 62px "${fontSecondary}"`;
  ctx.fillText(config.pageName ? config.pageName.toUpperCase() : 'ROCA PADEL TOUR', canvas.width / 2, startYText);
  return startYText;
};

export const drawCanvasBg = async (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, config: Config, isExport = true) => {
  const useExportBgUrl = isExport && config.exportBgUrl !== undefined && config.exportBgUrl !== '';
  const useExportBgColor = isExport && config.exportBgColor !== undefined && config.exportBgColor !== '';

  const finalBgColor = useExportBgColor ? config.exportBgColor : config.bgColor;
  const finalBgUrl = useExportBgUrl ? config.exportBgUrl : config.exportWithBg !== false ? config.bgUrl : null;

  if (finalBgUrl) {
    try {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = finalBgUrl;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      const canvasRatio = canvas.width / canvas.height;
      const imgRatio = img.width / img.height;
      let drawWidth, drawHeight, offsetX, offsetY;

      if (imgRatio > canvasRatio) {
        drawHeight = canvas.height;
        drawWidth = img.width * (canvas.height / img.height);
        offsetX = (canvas.width - drawWidth) / 2;
        offsetY = 0;
      } else {
        drawWidth = canvas.width;
        drawHeight = img.height * (canvas.width / img.width);
        offsetX = 0;
        offsetY = (canvas.height - drawHeight) / 2;
      }

      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      let finalAlpha = config.bgOpacity !== undefined ? config.bgOpacity : 85;
      if (isExport && config.exportBgOpacity !== undefined) {
        finalAlpha = config.exportBgOpacity;
      } else if (useExportBgUrl) {
        finalAlpha = 95;
      }

      ctx.fillStyle = finalBgColor ? getRgba(finalBgColor, finalAlpha) : `rgba(2, 6, 23, ${finalAlpha / 100})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } catch (e) {
      let finalAlpha = config.bgOpacity !== undefined ? config.bgOpacity : 100;
      if (isExport && config.exportBgOpacity !== undefined) {
        finalAlpha = config.exportBgOpacity;
      }
      ctx.fillStyle = finalBgColor ? getRgba(finalBgColor, finalAlpha) : '#0f172a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  } else {
    let finalAlpha = config.bgOpacity !== undefined ? config.bgOpacity : 100;
    if (isExport && config.exportBgOpacity !== undefined) {
      finalAlpha = config.exportBgOpacity;
    }
    ctx.fillStyle = finalBgColor ? getRgba(finalBgColor, finalAlpha) : '#0f172a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
};

export const handleShareCanvas = async (canvas: HTMLCanvasElement, filename: string, action: 'download' | 'whatsapp') => {
  const dataUrl = canvas.toDataURL('image/png');
  if (action === 'download') {
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    link.click();
    return;
  }
  if (action === 'whatsapp') {
    try {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Blob creation failed');
      const file = new File([blob], filename, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: 'Resultados Oficiales', files: [file] });
      } else {
        const link = document.createElement('a');
        link.download = filename;
        link.href = dataUrl;
        link.click();
      }
    } catch (error) {
      console.error('Error sharing:', error);
      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    }
  }
};

export const exportZonesAsImage = async (
  tournament: Tournament,
  config: Config,
  allTeams: Team[],
  clubs: Club[],
  action: 'download' | 'whatsapp'
) => {
  const zones = tournament.zones || [];
  if (zones.length === 0) return;

  const zonesPerRow = zones.length > 4 ? 3 : (zones.length === 1 ? 1 : 2);
  const zoneWidth = 540;
  const gap = 50;
  const canvasWidth = zonesPerRow === 1 ? zoneWidth + 160 : (zonesPerRow === 2 ? zoneWidth * 2 + gap + 160 : zoneWidth * 3 + gap * 2 + 160);
  
  const numRows = Math.ceil(zones.length / zonesPerRow);
  const expectedHeaderY = (config.exportWithBg !== false && config.logoUrl) ? 245 : 115;
  const startY = expectedHeaderY + 200;

  let totalZonesHeight = 0;
  for (let r = 0; r < numRows; r++) {
    const maxPrev = Math.max(...zones.slice(r * zonesPerRow, (r + 1) * zonesPerRow).map(z => z.teams?.length || 0), 0);
    totalZonesHeight += Math.max(380, 160 + maxPrev * 55 + 20) + gap;
  }
  const canvasHeight = startY + totalZonesHeight + 60 + (config.exportShowSponsors ? 280 : 0);

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  await drawCanvasBg(ctx, canvas, config, true);
  const headerY = await drawCanvasHeader(ctx, canvas, config);

  const fontSecondary = config.fontSecondary || 'sans-serif';
  const fontPrimary = config.fontPrimary || 'sans-serif';

  ctx.textAlign = 'center';
  ctx.fillStyle = config.secondaryTextColor || '#94a3b8';
  ctx.font = `bold 42px "${fontSecondary}"`;
  ctx.fillText(tournament.name.toUpperCase(), canvas.width / 2, headerY + 110);

  for (let i = 0; i < zones.length; i++) {
    const zone = zones[i];
    const col = i % zonesPerRow;
    const row = Math.floor(i / zonesPerRow);
    const maxTeamsInRowItems = zones.slice(
      row * zonesPerRow, 
      (row + 1) * zonesPerRow
    ).map(z => z.teams?.length || 0);
    const maxTeams = Math.max(...maxTeamsInRowItems, 0);
    const thisRowCardHeight = Math.max(380, 160 + maxTeams * 55 + 20);
    
    // We need to keep a running total of previous row heights
    let y = startY;
    for(let r=0; r<row; r++){
        const maxPrev = Math.max(...zones.slice(r * zonesPerRow, (r + 1) * zonesPerRow).map(z => z.teams?.length || 0), 0);
        y += Math.max(380, 160 + maxPrev * 55 + 20) + gap;
    }
    
    const rowWidth = Math.min(zones.length - row * zonesPerRow, zonesPerRow) * zoneWidth + (Math.min(zones.length - row * zonesPerRow, zonesPerRow) - 1) * gap;
    const rowStartX = (canvas.width - rowWidth) / 2;
    
    const x = rowStartX + col * (zoneWidth + gap);

    // Card
    const cardAlpha = config.exportCardOpacity !== undefined ? config.exportCardOpacity : (config.cardOpacity !== undefined ? config.cardOpacity : 70);
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = 35;
    ctx.fillStyle = getRgba(config.cardColor || '#0f172a', cardAlpha);
    ctx.beginPath();
    // @ts-ignore
    ctx.roundRect(x, y, zoneWidth, thisRowCardHeight, 30);
    ctx.fill();
    ctx.shadowBlur = 0;


    ctx.strokeStyle = config.primaryColor ? getRgba(config.primaryColor, 40) : 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.textAlign = 'left';
    ctx.fillStyle = config.primaryColor || '#eab308';
    ctx.font = `900 32px "${fontSecondary}"`;
    ctx.fillText(zone.name.toUpperCase(), x + 30, y + 55);

    // Table Header
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font = `bold 18px "${fontPrimary}"`;
    ctx.fillText('EQUIPO', x + 80, y + 105);
    ctx.textAlign = 'center';
    ctx.fillText('PJ', x + 360, y + 105);
    ctx.fillText('PTS', x + 430, y + 105);
    ctx.fillText('DIF', x + 500, y + 105);

    const sortedTeams = [...(zone.teams || [])].sort((a, b) => {
      if (b.pts !== a.pts) return b.pts - a.pts;
      const diffB = (b.sf || 0) - (b.sc || 0);
      const diffA = (a.sf || 0) - (a.sc || 0);
      return diffB - diffA;
    });

    for (let idx = 0; idx < sortedTeams.length; idx++) {
      const zt = sortedTeams[idx];
      const rowY = y + 160 + idx * 55;
      const team = allTeams.find((t) => t.id === zt.teamId);
      const diff = (zt.sf || 0) - (zt.sc || 0);

      const isQualifying = tournament.hasPlayoffs && tournament.qualifiersPerZone && (idx < tournament.qualifiersPerZone);

      if (isQualifying) {
        ctx.fillStyle = config.primaryColor ? getRgba(config.primaryColor, 10) : 'rgba(var(--glow-rgb), 0.1)';
        ctx.fillRect(x + 15, rowY - 35, zoneWidth - 30, 50);
        
        ctx.fillStyle = config.primaryColor || '#eab308';
        ctx.globalAlpha = 0.4;
        ctx.fillRect(x + 15, rowY - 35, 3, 50);
        ctx.globalAlpha = 1.0;
      } else if (idx % 2 === 0) {
        ctx.fillStyle = 'rgba(255,255,255,0.05)';
        ctx.fillRect(x + 15, rowY - 35, zoneWidth - 30, 50);
      }

      // Team Logo
      if (team?.photoUrl) {
        try {
          const tLogo = new Image();
          tLogo.crossOrigin = 'Anonymous';
          tLogo.src = team.photoUrl;
          await new Promise((res) => {
            tLogo.onload = res;
            tLogo.onerror = res;
          });
          ctx.save();
          ctx.beginPath();
          ctx.arc(x + 45, rowY - 10, 20, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(tLogo, x + 25, rowY - 30, 40, 40);
          ctx.restore();
        } catch (e) {}
      } else {
        ctx.fillStyle = 'rgba(255,255,255,0.1)';
        ctx.beginPath();
        ctx.arc(x + 45, rowY - 10, 20, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.textAlign = 'left';
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold 22px "${fontPrimary}"`;
      ctx.fillText(`${team?.name || '??'}`, x + 80, rowY);

      ctx.textAlign = 'center';
      ctx.font = `22px "${fontPrimary}"`;
      ctx.fillText(zt.pj.toString(), x + 360, rowY);
      ctx.fillStyle = config.primaryColor || '#eab308';
      ctx.font = `black 24px "${fontPrimary}"`;
      ctx.fillText(zt.pts.toString(), x + 430, rowY);
      ctx.fillStyle = '#ffffff';
      ctx.font = `22px "${fontPrimary}"`;
      ctx.fillText(diff > 0 ? `+${diff}` : diff.toString(), x + 500, rowY);
    }
  }

  await drawExportFooter(ctx, canvas, config, clubs);
  drawExportBorder(ctx, canvas, config);
  await handleShareCanvas(canvas, `tablas-${tournament.name}.png`, action);
};

export const exportBracketAsImage = async (
  tournament: Tournament,
  config: Config,
  allTeams: Team[],
  clubs: Club[],
  action: 'download' | 'whatsapp'
) => {
  const brackets = tournament.brackets || [];
  if (brackets.length === 0) return;

  const expectedHeaderY = (config.exportWithBg !== false && config.logoUrl) ? 245 : 115;
  const startYBase = expectedHeaderY + 260;

  const finalRound = brackets.find((b) => b.matches.length === 1);
  const otherRounds = brackets.filter((b) => b.matches.length > 1).sort((a, b) => b.matches.length - a.matches.length);

  const columnsPerSide = otherRounds.length;
  const matchWidth = 340;
  const matchHeight = 150;
  const colGap = 120;
  const rowGap = 60; // Better spacing minimum

  const maxMatchesOnOneSide = Math.max(1, ...otherRounds.map(r => Math.ceil(r.matches.length / 2)));
  const requiredHeight = maxMatchesOnOneSide * (matchHeight + rowGap) - rowGap;

  const canvasWidth = Math.max(1600, (columnsPerSide * 2 + 1) * matchWidth + (columnsPerSide * 2) * colGap + 200);
  const canvasHeight = startYBase + requiredHeight + 150 + (config.exportShowSponsors ? 280 : 0);
  
  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  await drawCanvasBg(ctx, canvas, config, true);
  const headerY = await drawCanvasHeader(ctx, canvas, config);

  const fontSecondary = config.fontSecondary || 'sans-serif';
  const fontPrimary = config.fontPrimary || 'sans-serif';

  ctx.textAlign = 'center';
  ctx.fillStyle = config.secondaryTextColor || '#94a3b8';
  ctx.font = `bold 42px "${fontSecondary}"`;
  ctx.fillText(`PLAYOFFS: ${tournament.name.toUpperCase()}`, canvas.width / 2, headerY + 110);

  const getTeam = (id: string) => allTeams.find((t) => t.id === id);

  const drawMatch = async (m: any, x: number, y: number, w: number, h: number, roundName?: string) => {
    const cardAlpha = config.exportCardOpacity !== undefined ? config.exportCardOpacity : (config.cardOpacity !== undefined ? config.cardOpacity : 70);
    
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = 30;
    ctx.fillStyle = getRgba(config.cardColor || '#0f172a', cardAlpha);
    ctx.beginPath();
    // @ts-ignore
    ctx.roundRect(x, y, w, h, 20);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = config.primaryColor ? getRgba(config.primaryColor, 50) : 'rgba(255,255,255,0.2)';
    ctx.lineWidth = 3;
    ctx.stroke();

    if (roundName) {
      ctx.textAlign = 'center';
      ctx.fillStyle = config.primaryColor || '#eab308';
      ctx.font = `900 16px "${fontPrimary}"`;
      ctx.fillText(roundName.toUpperCase(), x + w / 2, y + 30);
    }

    const drawTeamRow = async (teamId: string, score: string, isWinner: boolean, rowY: number) => {
      const team = getTeam(teamId);
      
      if (team?.photoUrl) {
        try {
          const img = new Image();
          img.crossOrigin = 'Anonymous';
          img.src = team.photoUrl;
          await new Promise((res) => {
            img.onload = res;
            img.onerror = res;
          });
          ctx.save();
          ctx.beginPath();
          ctx.arc(x + 40, rowY, 22, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(img, x + 18, rowY - 22, 44, 44);
          ctx.restore();
        } catch (e) {}
      } else {
        ctx.fillStyle = 'rgba(255,255,255,0.15)';
        ctx.beginPath();
        ctx.arc(x + 40, rowY, 22, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.textAlign = 'left';
      ctx.font = isWinner ? `bold 24px "${fontPrimary}"` : `20px "${fontPrimary}"`;
      ctx.fillStyle = isWinner ? config.primaryColor || '#eab308' : (teamId ? '#f1f5f9' : 'rgba(255,255,255,0.4)');
      ctx.fillText(team?.name || 'A DEFINIR', x + 80, rowY + 10);

      ctx.textAlign = 'right';
      ctx.fillStyle = isWinner ? config.primaryColor || '#eab308' : '#ffffff';
      ctx.font = `900 28px "${fontPrimary}"`;
      ctx.fillText(score || '-', x + w - 25, rowY + 10);
    };

    const s1 = m.score ? m.score.split('-')[0] : '';
    const s2 = m.score ? m.score.split('-')[1] : '';

    await drawTeamRow(m.team1Id, s1, m.winnerId === m.team1Id && m.team1Id, y + 75);
    
    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.beginPath();
    ctx.moveTo(x + 20, y + 95);
    ctx.lineTo(x + w - 20, y + 95);
    ctx.stroke();

    await drawTeamRow(m.team2Id, s2, m.winnerId === m.team2Id && m.team2Id, y + 115);
  };

  const matchPositions = new Map();

  for (let i = 0; i < otherRounds.length; i++) {
    const r = otherRounds[i];
    
    const distFromCenter = (columnsPerSide - i) * (matchWidth + colGap);
    const xLeft = canvas.width / 2 - distFromCenter - matchWidth / 2;
    const xRight = canvas.width / 2 + distFromCenter - matchWidth / 2;
    
    const half = Math.ceil(r.matches.length / 2);
    const leftMatches = r.matches.slice(0, half);
    const rightMatches = r.matches.slice(half);

    const leftBlockSize = requiredHeight / (leftMatches.length || 1);
    for (let j = 0; j < leftMatches.length; j++) {
      const verticalPos = startYBase + j * leftBlockSize + (leftBlockSize / 2) - (matchHeight / 2);
      matchPositions.set(`L-${i}-${j}`, { x: xLeft, y: verticalPos, w: matchWidth, h: matchHeight, m: leftMatches[j], roundName: r.round });
    }

    const rightBlockSize = requiredHeight / (rightMatches.length || 1);
    for (let j = 0; j < rightMatches.length; j++) {
      const verticalPos = startYBase + j * rightBlockSize + (rightBlockSize / 2) - (matchHeight / 2);
      matchPositions.set(`R-${i}-${j}`, { x: xRight, y: verticalPos, w: matchWidth, h: matchHeight, m: rightMatches[j], roundName: r.round });
    }
  }

  if (finalRound && finalRound.matches.length > 0) {
    const centerX = canvas.width / 2 - matchWidth / 2;
    const centerY = startYBase + (requiredHeight / 2) - (matchHeight / 2);
    matchPositions.set('FINAL', { x: centerX, y: centerY, w: matchWidth, h: matchHeight, m: finalRound.matches[0], roundName: finalRound.round, isFinal: true });
  }

  // Draw connecting lines based on progression BEFORE matches
  const allEntries = Array.from(matchPositions.values()) as any[];

  const drawBezierLine = (startX: number, startY: number, endX: number, endY: number, A_is_left: boolean, hasWinner: boolean) => {
    let cp1x, cp2x;
    if (A_is_left) {
      cp1x = startX + colGap / 2;
      cp2x = endX - colGap / 2;
    } else {
      cp1x = startX - colGap / 2;
      cp2x = endX + colGap / 2;
    }

    ctx.beginPath();
    ctx.lineWidth = hasWinner ? 4 : 2;
    ctx.strokeStyle = hasWinner ? (config.primaryColor || '#eab308') : 'rgba(255, 255, 255, 0.15)';
    ctx.moveTo(startX, startY);
    ctx.bezierCurveTo(cp1x, startY, cp2x, endY, endX, endY);
    ctx.stroke();
  };

  // Base faint lines for all bracket connections by generic map keys
  for (let i = 0; i < otherRounds.length; i++) {
    const isLastRegularRound = i === otherRounds.length - 1;
    const r = otherRounds[i];
    const half = Math.ceil(r.matches.length / 2);

    for (let j = 0; j < half; j++) {
      const matchA = matchPositions.get(`L-${i}-${j}`);
      const matchB = isLastRegularRound ? matchPositions.get('FINAL') : matchPositions.get(`L-${i + 1}-${Math.floor(j / 2)}`);
      
      if (matchA && matchB) {
        const startX = matchA.x + matchA.w;
        const endX = matchB.x;
        const startY = matchA.y + matchA.h / 2;
        const endY = matchB.y + matchB.h / 2;
        const hasWinner = matchA.m.winnerId && (matchB.m.team1Id === matchA.m.winnerId || matchB.m.team2Id === matchA.m.winnerId);
        
        drawBezierLine(startX, startY, endX, endY, true, hasWinner);
      }
    }

    for (let j = 0; j < r.matches.length - half; j++) {
      const matchA = matchPositions.get(`R-${i}-${j}`);
      const matchB = isLastRegularRound ? matchPositions.get('FINAL') : matchPositions.get(`R-${i + 1}-${Math.floor(j / 2)}`);
      
      if (matchA && matchB) {
        const startX = matchA.x;
        const endX = matchB.x + matchB.w;
        const startY = matchA.y + matchA.h / 2;
        const endY = matchB.y + matchB.h / 2;
        const hasWinner = matchA.m.winnerId && (matchB.m.team1Id === matchA.m.winnerId || matchB.m.team2Id === matchA.m.winnerId);
        
        drawBezierLine(startX, startY, endX, endY, false, hasWinner);
      }
    }
  }

  // Draw Matches OVER lines
  for (const pos of allEntries) {
    if (pos.isFinal) {
      ctx.textAlign = 'center';
      ctx.fillStyle = config.secondaryColor || '#fbbf24';
      ctx.font = `900 48px "${fontSecondary}"`;
      ctx.fillText('GRAN FINAL', canvas.width / 2, pos.y - 60);
    }
    await drawMatch(pos.m, pos.x, pos.y, pos.w, pos.h, pos.roundName);
  }

  await drawExportFooter(ctx, canvas, config, clubs);
  drawExportBorder(ctx, canvas, config);
  await handleShareCanvas(canvas, `playoffs-${tournament.name}.png`, action);
};
